import { Injectable, Injector, NgZone, OnDestroy, runInInjectionContext } from '@angular/core';
import { BehaviorSubject, combineLatest, from, Observable, Subscription } from 'rxjs';
import { EditorRequest, Member, Team, TeamRole } from '../shared/models/models';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { map, retry, shareReplay, switchMap, take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Auth, user } from '@angular/fire/auth';
import {
  collection,
  collectionData,
  DocumentData,
  Firestore,
  orderBy,
  query,
  where,
  CollectionReference,
  DocumentReference,
  doc,
  docData,
  deleteDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root',
})
export class TeamsService implements OnDestroy {
  teamsRef: BehaviorSubject<CollectionReference> = new BehaviorSubject(null); // reference to the collection of teams docs
  teamsList: Observable<DocumentData[]>; // observable of the user's list of teams and IDs
  viewerTeamsList: Observable<DocumentData[]>; // observable of the user's list of teams and IDs
  selectedTeamRef: DocumentReference<DocumentData>; // reference to the firestore document of the currently selected team

  // BehaviorSubject for the currently selected team - emits values rather than reassigning Observable
  private _currentlySelectedTeam: BehaviorSubject<DocumentData> = new BehaviorSubject(null);
  currentlySelectedTeam: Observable<DocumentData> = this._currentlySelectedTeam.asObservable();
  private teamSubscription: Subscription;

  constructor(
    // private db: AngularFirestore, // Instance of the firestore database
    private firestore: Firestore,
    private functions: Functions, // Instance of the cloud functions api
    private auth: Auth,
    private http: HttpClient,
    private zone: NgZone,
    private injector: Injector,
  ) {}

  ngOnDestroy(): void {
    if (this.teamSubscription) {
      this.teamSubscription.unsubscribe();
    }
  }

  // //
  // // Gets the teams associated with a specific user Id
  // //
  // getUsersTeams(uid: string): Observable<(Team & { id: string; })[]> {
  //   this.teamsRef.next(this.db.collection<Team>('teams', ref => ref.where('owner', '==', uid).orderBy('created', 'asc')));
  //   return this.teamsList = this.teamsRef.value.valueChanges({ idField: 'id' });
  // }
  //
  // Gets the teams associated with a specific user Id
  //
  getUsersTeams(uid: string, email: string): Observable<DocumentData[]> {
    return (this.teamsList = runInInjectionContext(this.injector, () => {
      const col = collection(this.firestore, 'teams');
      const ownerQ = query(
        col,
        where('owner', '==', uid),
        orderBy('created', 'asc'),
      );
      const viewerQ = query(
        col,
        where('viewers', 'array-contains', { email: email, uid: uid }),
        orderBy('created', 'asc'),
      );

      return combineLatest([
        collectionData(ownerQ, { idField: 'id' }),
        collectionData(viewerQ, { idField: 'id' }),
      ]).pipe(map((arr) => arr.reduce((acc, cur) => acc.concat(cur))));
    }));
  }

  //
  // Adds or removes a team from the user's favorites, stored on their own users/{uid} doc
  // (the live user doc feeds currentUser, so the UI updates on its own).
  //
  setFavoriteTeam(uid: string, teamId: string, favorite: boolean): Observable<void> {
    return this.setUserTeamFlag(uid, 'favoriteTeamIds', teamId, favorite);
  }

  //
  // Hides or unhides a team in the user's own lists (All Teams and the sidebar). Personal
  // organization only: the team, its members and plan limits are unaffected.
  //
  setHiddenTeam(uid: string, teamId: string, hidden: boolean): Observable<void> {
    return this.setUserTeamFlag(uid, 'hiddenTeamIds', teamId, hidden);
  }

  private setUserTeamFlag(
    uid: string,
    field: 'favoriteTeamIds' | 'hiddenTeamIds',
    teamId: string,
    on: boolean,
  ): Observable<void> {
    // Firestore promises can settle outside Angular's zone; runInZone keeps toasts and other
    // follow-up UI updates rendering.
    return from(
      runInInjectionContext(this.injector, () =>
        updateDoc(doc(this.firestore, `users/${uid}`), {
          [field]: on ? arrayUnion(teamId) : arrayRemove(teamId),
        }),
      ),
    ).pipe(this.runInZone());
  }

  //
  // Gets only the viewable teams associated with a specific user Id
  //
  getViewableTeams(uid: string, email: string): Observable<DocumentData[]> {
    return (this.teamsList = runInInjectionContext(this.injector, () => {
      const col = collection(this.firestore, 'teams');
      // const ownerQ = query(col, where('owner', '==', uid), orderBy('created', 'asc'));
      const viewerQ = query(
        col,
        where('viewers', 'array-contains', { email: email, uid: uid }),
        orderBy('created', 'asc'),
      );

      return collectionData(viewerQ, { idField: 'id' });
    }));
  }

  //
  // Gets the teams where the user is listed as a 'viewer'
  //
  // getUsersViewerTeams(uid: string): Observable<DocumentData[]> {
  //   const col = collection(this.firestore, 'teams');
  //   const q = query(col, where('viewers', 'array-contains', uid), orderBy('created', 'asc'));

  //   return this.viewerTeamsList = collectionData(q, { idField: 'id' });
  // }

  //
  // Creates a new team from an object
  //
  createTeam(team: Team): Observable<any> {
    const url = `https://functions.mpactq.com/teams/`;

    return user(this.auth).pipe(
      take(1),
      switchMap((currentUser) => {
        return from(currentUser.getIdToken()).pipe(
          take(1),
          switchMap((token) => {
            return this.http
              .put(url, team, {
                headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                responseType: 'json',
              })
              .pipe(retry(3), shareReplay());
          }),
        );
      }),
    );
  }

  //
  // Updates the currentlySelected team - uses BehaviorSubject to emit values so existing subscriptions receive updates
  //
  selectTeam(id: string): Observable<DocumentData> {
    // Unsubscribe from previous team document if exists
    if (this.teamSubscription) {
      this.teamSubscription.unsubscribe();
    }

    // Subscribe to the new team document and emit values through the BehaviorSubject
    this.teamSubscription = runInInjectionContext(this.injector, () =>
      docData(doc(this.firestore, `teams/${id}`), { idField: 'id' }),
    ).subscribe(team => {
      this._currentlySelectedTeam.next(team);
    });

    return this.currentlySelectedTeam;
  }

  //
  // Deletes a team
  //
  deleteTeam(id: string) {
    // this.db.collection<Team>('teams').doc(id).delete(); // delete the team document from firebase
    return deleteDoc(doc(this.firestore, `teams/${id}`));
  }

  //
  // Removes a team member from a team via the removeTeamMember callable.
  // Notes/goals cleanup runs server-side as part of the same call.
  //
  deleteTeamMember(teamId: string, member: Member): Observable<any> {
    const removeFn = httpsCallable(this.functions, 'removeTeamMember');
    return from(removeFn({ teamId, memberUid: member.uid })).pipe(
      map((result) => result.data),
      this.runInZone(),
    );
  }

  //
  // Adds team members to the current team and starts any request workflows
  //
  addTeamMember(
    teamId: string,
    members: {
      email: string;
      purchase: boolean;
      firstName: string;
      lastName: string;
    }[],
  ): Observable<any> {
    // const addMembers = this.fns.httpsCallable('createTeamRequests'); // create an instance of the createTeamRequests cloud function

    // return addMembers({ // call it with the teamId and list of team member ids and return an observable with the result
    //   teamId: teamId,
    //   reqUserEmails: members,
    // });

    const url = `https://functions.mpactq.com/teams/${teamId}/addMembers`;

    return user(this.auth).pipe(
      take(1),
      switchMap((currentUser) => {
        return from(currentUser.getIdToken()).pipe(
          take(1),
          switchMap((token) => {
            return this.http
              .post(
                url,
                {
                  newMembers: members,
                },
                {
                  headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  responseType: 'json',
                },
              )
              .pipe(retry(3), shareReplay());
          }),
        );
      }),
    );

    // return this.http.post(
    //   url,
    //   {
    //     reqUserEmails: members
    //   },
    //   {
    //     headers: {
    //       'Accept': 'application/json',
    //       'Content-Type': 'application/json',
    //       'Authorization': `Bearer ${this.accountService.currentUser.value.token}`
    //     },
    //     responseType: 'json',
    //   }
    // ).pipe(
    //   retry(3),
    //   shareReplay()
    // );
  }

  //
  // Cancels (deletes) a team request via the cancelTeamRequest callable so
  // editors can cancel too and the server can audit-log the action.
  //
  cancelTeamMemberRequest(teamId: string, reqUserId: string): Observable<any> {
    const cancelFn = httpsCallable(this.functions, 'cancelTeamRequest');
    return from(cancelFn({ teamId, requestedUserId: reqUserId })).pipe(
      map((result) => result.data),
      this.runInZone(),
    );
  }

  resendTeamMemberRequest(reqUserId: string): Observable<any> {
    const resendFn = httpsCallable(this.functions, 'resendTeamRequest');

    return from(resendFn({ reqUserID: reqUserId })).pipe(map((result) => result.data), this.runInZone());
  }

  resendTeamRequestByTeamOwner(teamOwnerId: string, reqUserId: string): Observable<any> {
    const resendFn = httpsCallable(this.functions, 'resendTeamRequestByTeamOwner');

    return from(resendFn({ teamOwnerId, reqUserID: reqUserId })).pipe(map((result) => result.data), this.runInZone());
  }

  //
  // 30s memo cache for getTeamInsights. Dashboard re-mounts hit the same
  // (team-id, member-roster) tuple repeatedly; cached read = no network.
  // updateTeam() / addMembers / removeMembers below invalidate the cache.
  //
  private insightsCache = new Map<string, { ts: number; value$: Observable<any> }>();
  private insightsTtlMs = 30_000;

  private insightsCacheKey(team: any): string {
    const memberIds = (team?.members ?? [])
      .map((m: any) => m?.uid ?? m?.email ?? '')
      .sort()
      .join(',');
    return `${team?.id ?? team?.name ?? ''}|${memberIds}`;
  }

  invalidateInsightsCache(teamId?: string) {
    if (!teamId) {
      this.insightsCache.clear();
      return;
    }
    for (const key of Array.from(this.insightsCache.keys())) {
      if (key.startsWith(`${teamId}|`)) this.insightsCache.delete(key);
    }
  }

  //
  // Gets the team insights for a team
  //
  getTeamInsights(team: any): Observable<any> {
    const key = this.insightsCacheKey(team);
    const existing = this.insightsCache.get(key);
    if (existing && Date.now() - existing.ts < this.insightsTtlMs) {
      return existing.value$;
    }
    const getInsightsFn = httpsCallable(this.functions, 'getTeamInsights');
    const value$ = from(getInsightsFn(team)).pipe(
      map((result) => result.data),
      this.runInZone(),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.insightsCache.set(key, { ts: Date.now(), value$ });
    return value$;
  }

  //
  // Updates the name and description of a team via the updateTeam callable.
  // Routed server-side so editors are gated and the change is audit-logged.
  //
  updateTeam(team: { id: string; name?: string; description?: string }): Observable<any> {
    const updateFn = httpsCallable(this.functions, 'updateTeam');
    return from(
      updateFn({
        teamId: team.id,
        name: team.name,
        description: team.description,
      }),
    ).pipe(map((result) => result.data), this.runInZone());
  }

  //
  // Reorders members on a team via the updateTeam callable. members[] is
  // restricted by the Firestore rule for editors, so reordering has to go
  // through the server path that runs the role check + audit log.
  //
  updateTeamMemberOrder(team: any): Observable<any> {
    const reorderFn = httpsCallable(this.functions, 'updateTeam');
    return from(
      reorderFn({
        teamId: team.id,
        members: team.members.map((m: any) => ({ ...m, display: true })),
      }),
    ).pipe(map((result) => result.data), this.runInZone());
  }

  //
  // Remove viewer from team
  //
  // removeTeamViewer(teamId: string, viewer: {email: string, uid: string}) {
  //   updateDoc(doc(this.firestore, `teams/${teamId}`), {
  //     viewers: firebase.default.firestore.FieldValue.arrayRemove({ // remove viewer from array of viewers
  //       ...viewer
  //     })
  //   }).then((res) => { // After the team member is removed we need to clean up their notes and goals
  //     console.log("Team viewer removed successfully.", res);
  //   }).catch(err => { // handle errors from removing the team member
  //     console.log("There was an error removing the team member: ", err); // log error
  //   });
  // }

  //
  // Add team viewers to team record
  //
  addTeamViewers(teamId: string, viewers: string[]) {
    const url = `https://functions.mpactq.com/teams/${teamId}/addViewers`;

    return user(this.auth).pipe(
      take(1),
      switchMap((currentUser) => {
        return from(currentUser.getIdToken()).pipe(
          take(1),
          switchMap((token) => {
            return this.http
              .post(
                url,
                {
                  newViewers: viewers,
                },
                {
                  headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  responseType: 'json',
                },
              )
              .pipe(retry(3), shareReplay());
          }),
        );
      }),
    );
  }

  //
  // Remove team viewers from team record
  //
  removeTeamViewer(teamId: string, viewers: string[]) {
    const url = `https://functions.mpactq.com/teams/${teamId}/rmViewers`;

    return user(this.auth).pipe(
      take(1),
      switchMap((currentUser) => {
        return from(currentUser.getIdToken()).pipe(
          take(1),
          switchMap((token) => {
            return this.http
              .post(
                url,
                {
                  rmViewers: viewers,
                },
                {
                  headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  responseType: 'json',
                },
              )
              .pipe(retry(3), shareReplay());
          }),
        );
      }),
    );
  }

  //
  // Returns the url of the couplet defined by an id
  //
  getIconUrl(id: number, prod?: boolean) {
    return (
      'https://pathfinder-assessment-service.vercel.app/api/dashboard/icon/' +
      id
    ); // return the url
  }

  //
  // Resolve the caller's STRUCTURAL role on a team. Mirrors the server
  // helper: admin is carried separately, not flattened into 'owner', so the
  // UI doesn't misreport (e.g. show "Manage Editors" to an admin viewing
  // someone else's team, or hide the badge for admins).
  //
  // Use canManageTeam/canDeleteTeam/canManageEditors with both args to get
  // permission decisions that include the admin bypass.
  //
  getRole(team: Team | null | undefined, uid: string | undefined): TeamRole {
    if (!team || !uid) return 'none';
    if (team.owner === uid) return 'owner';
    if (Array.isArray(team.editors) && team.editors.includes(uid)) return 'editor';
    if (Array.isArray(team.viewers) && team.viewers.some((v) => v?.uid === uid)) return 'viewer';
    if (Array.isArray(team.members) && team.members.some((m) => m?.uid === uid)) return 'member';
    return 'none';
  }

  canManageTeam(role: TeamRole, isAdmin = false): boolean {
    return isAdmin || role === 'owner' || role === 'editor';
  }

  canDeleteTeam(role: TeamRole, isAdmin = false): boolean {
    return isAdmin || role === 'owner';
  }

  canManageEditors(role: TeamRole, isAdmin = false): boolean {
    return isAdmin || role === 'owner';
  }

  // Firebase SDK promises can settle outside Angular's zone (the SDK ships
  // native async internals zone.js cannot track), so subscriber callbacks
  // mutate component state without triggering change detection — e.g. the
  // add-editor spinner hung forever (ClickUp 86e1ru0v4 / 86e1ru433).
  // Re-enter the zone at the service boundary so emissions land inside Angular.
  private runInZone<T>() {
    return (source: Observable<T>): Observable<T> =>
      new Observable<T>((subscriber) => {
        const sub = source.subscribe({
          next: (value) => this.zone.run(() => subscriber.next(value)),
          error: (err) => this.zone.run(() => subscriber.error(err)),
          complete: () => this.zone.run(() => subscriber.complete()),
        });
        return () => sub.unsubscribe();
      });
  }

  //
  // Editor invitation API
  //
  addEditor(teamId: string, email: string): Observable<{ requestId: string; invitedUid: string }> {
    const fn = httpsCallable(this.functions, 'addTeamEditor');
    return from(fn({ teamId, email })).pipe(
      map((result) => result.data as { requestId: string; invitedUid: string }),
      this.runInZone(),
    );
  }

  removeEditor(teamId: string, editorUid: string): Observable<any> {
    const fn = httpsCallable(this.functions, 'removeTeamEditor');
    return from(fn({ teamId, editorUid })).pipe(map((result) => result.data), this.runInZone());
  }

  acceptEditorInvite(requestId: string): Observable<any> {
    const fn = httpsCallable(this.functions, 'acceptEditorRequest');
    return from(fn({ requestId })).pipe(map((result) => result.data), this.runInZone());
  }

  declineEditorInvite(requestId: string): Observable<any> {
    const fn = httpsCallable(this.functions, 'declineEditorRequest');
    return from(fn({ requestId })).pipe(map((result) => result.data), this.runInZone());
  }

  revokeEditorInvite(requestId: string): Observable<any> {
    const fn = httpsCallable(this.functions, 'revokeEditorRequest');
    return from(fn({ requestId })).pipe(map((result) => result.data), this.runInZone());
  }

  getPendingInvitesForUser(uid: string): Observable<(EditorRequest & { id: string })[]> {
    return runInInjectionContext(this.injector, () => {
      const col = collection(this.firestore, 'editorRequests');
      const q = query(
        col,
        where('invitedUid', '==', uid),
        where('status', '==', 'pending'),
      );
      return collectionData(q, { idField: 'id' }) as Observable<(EditorRequest & { id: string })[]>;
    });
  }

  getPendingInvitesForTeam(teamId: string): Observable<(EditorRequest & { id: string })[]> {
    return runInInjectionContext(this.injector, () => {
      const col = collection(this.firestore, 'editorRequests');
      const q = query(
        col,
        where('teamId', '==', teamId),
        where('status', '==', 'pending'),
      );
      return collectionData(q, { idField: 'id' }) as Observable<(EditorRequest & { id: string })[]>;
    });
  }
}
